import './UserProfileCard.scss';

import { type FC, useCallback, useMemo, useRef, useState } from 'react';

import { extraRoleCount, primaryRole, rolesLabel } from '../../../../authz/role-display';
import { getUserData } from '../../../../constants/global';
import { useTheme } from '../../../../context/theme';
import { UserAvatar } from './UserAvatar';
import { UserDropdownMenu } from './UserDropdownMenu';
import type { UserDropdownMenuItem, UserProfileCardProps } from './UserProfileCard.types';

const ChevronUpIcon = () => (
	<svg
		width='14'
		height='14'
		viewBox='0 0 14 14'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'
	>
		<path
			d='M3.5 8.75L7 5.25L10.5 8.75'
			stroke='currentColor'
			strokeWidth='1.5'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

const UserIcon = () => (
	<svg
		width='18'
		height='18'
		viewBox='0 0 24 24'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'
	>
		<path
			d='M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z'
			stroke='currentColor'
			strokeWidth='1.5'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
		<path
			d='M20.59 22C20.59 18.13 16.74 15 12 15C7.26 15 3.41 18.13 3.41 22'
			stroke='currentColor'
			strokeWidth='1.5'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

const SettingsIcon = () => (
	<svg
		width='18'
		height='18'
		viewBox='0 0 24 24'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'
	>
		<path
			d='M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z'
			stroke='currentColor'
			strokeWidth='1.5'
			strokeMiterlimit='10'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
		<path
			d='M2 12.88V11.12C2 10.08 2.85 9.22 3.9 9.22C5.71 9.22 6.45 7.94 5.54 6.37C5.02 5.47 5.33 4.3 6.24 3.78L7.97 2.79C8.76 2.32 9.78 2.6 10.25 3.39L10.36 3.58C11.26 5.15 12.74 5.15 13.65 3.58L13.76 3.39C14.23 2.6 15.25 2.32 16.04 2.79L17.77 3.78C18.68 4.3 18.99 5.47 18.47 6.37C17.56 7.94 18.3 9.22 20.11 9.22C21.15 9.22 22.01 10.07 22.01 11.12V12.88C22.01 13.92 21.16 14.78 20.11 14.78C18.3 14.78 17.56 16.06 18.47 17.63C18.99 18.54 18.68 19.7 17.77 20.22L16.04 21.21C15.25 21.68 14.23 21.4 13.76 20.61L13.65 20.42C12.75 18.85 11.27 18.85 10.36 20.42L10.25 20.61C9.78 21.4 8.76 21.68 7.97 21.21L6.24 20.22C5.33 19.7 5.02 18.53 5.54 17.63C6.45 16.06 5.71 14.78 3.9 14.78C2.85 14.78 2 13.92 2 12.88Z'
			stroke='currentColor'
			strokeWidth='1.5'
			strokeMiterlimit='10'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

const SunIcon = () => (
	<svg
		width='18'
		height='18'
		viewBox='0 0 24 24'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'
	>
		<path
			d='M12 18.5C15.5899 18.5 18.5 15.5899 18.5 12C18.5 8.41015 15.5899 5.5 12 5.5C8.41015 5.5 5.5 8.41015 5.5 12C5.5 15.5899 8.41015 18.5 12 18.5Z'
			stroke='currentColor'
			strokeWidth='1.5'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
		<path
			d='M19.14 19.14L19.01 19.01M19.01 4.99L19.14 4.86L19.01 4.99ZM4.86 19.14L4.99 19.01L4.86 19.14ZM12 2.08V2V2.08ZM12 22V21.92V22ZM2.08 12H2H2.08ZM22 12H21.92H22ZM4.99 4.99L4.86 4.86L4.99 4.99Z'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

const MoonIcon = () => (
	<svg
		width='18'
		height='18'
		viewBox='0 0 24 24'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'
	>
		<path
			d='M2.03 12.42C2.39 17.57 6.76 21.76 11.99 21.99C15.68 22.15 18.98 20.43 20.96 17.72C21.78 16.61 21.34 15.87 19.97 16.12C19.3 16.24 18.61 16.29 17.89 16.26C13.0 16.06 9.00001 11.97 8.98001 7.14001C8.97001 5.84001 9.24 4.61001 9.73 3.49001C10.27 2.25001 9.62 1.66001 8.37 2.19001C4.41 3.86001 1.7 7.85001 2.03 12.42Z'
			stroke='currentColor'
			strokeWidth='1.5'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

const LogoutIcon = () => (
	<svg
		width='18'
		height='18'
		viewBox='0 0 24 24'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'
	>
		<path
			d='M8.90002 7.56001C9.21002 3.96001 11.06 2.49001 15.11 2.49001H15.24C19.71 2.49001 21.5 4.28001 21.5 8.75001V15.27C21.5 19.74 19.71 21.53 15.24 21.53H15.11C11.09 21.53 9.24002 20.08 8.91002 16.54'
			stroke='currentColor'
			strokeWidth='1.5'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
		<path
			d='M15 12H3.62'
			stroke='currentColor'
			strokeWidth='1.5'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
		<path
			d='M5.85 8.65002L2.5 12L5.85 15.35'
			stroke='currentColor'
			strokeWidth='1.5'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

export const UserProfileCard: FC<UserProfileCardProps> = ({ onLogout, onOpenAccount, onOpenSettings }) => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const { toggleTheme, isDark } = useTheme();

	const userData = getUserData()?.user;
	const userName = userData?.FullName || 'User';

	// Show the role that explains the most access, not whichever the DB returned first —
	// a `Party Admin` + `Super Admin` account wields both, and labelling it "Party Admin"
	// makes system-level modules look like a permissions bug.
	const roles = useMemo(
		() => (Array.isArray(userData?.role) ? userData.role : userData?.role ? [userData.role] : []),
		[userData?.role]
	);
	const userRole = primaryRole(roles);
	const otherRoles = extraRoleCount(roles);

	const handleToggleMenu = useCallback(() => {
		setIsMenuOpen((prev) => !prev);
	}, []);

	const handleCloseMenu = useCallback(() => {
		setIsMenuOpen(false);
	}, []);

	const menuItems: UserDropdownMenuItem[] = useMemo(
		() => [
			{
				id: 'account',
				label: 'My Account',
				description: 'View and edit your profile',
				icon: <UserIcon />,
				action: () => {
					onOpenAccount?.();
				},
			},
			{
				id: 'settings',
				label: 'Settings',
				description: 'Preferences and configuration',
				icon: <SettingsIcon />,
				action: () => {
					onOpenSettings?.();
				},
			},
			{
				id: 'theme',
				label: isDark ? 'Light Mode' : 'Dark Mode',
				description: isDark ? 'Switch to light theme' : 'Switch to dark theme',
				icon: isDark ? <SunIcon /> : <MoonIcon />,
				action: toggleTheme,
			},
			{
				id: 'logout',
				label: 'Sign Out',
				icon: <LogoutIcon />,
				action: onLogout,
				variant: 'destructive',
			},
		],
		[isDark, toggleTheme, onLogout, onOpenAccount, onOpenSettings]
	);

	return (
		<div className='user-profile-card'>
			<button
				ref={triggerRef}
				type='button'
				className={`user-profile-card__trigger ${isMenuOpen ? 'user-profile-card__trigger--active' : ''}`}
				onClick={handleToggleMenu}
				aria-haspopup='menu'
				aria-expanded={isMenuOpen}
				aria-label={`User menu for ${userName}. ${rolesLabel(roles)}`}
			>
				<UserAvatar
					name={userName}
					size='small'
				/>

				<div className='user-profile-card__info'>
					<span className='user-profile-card__name'>{userName}</span>
					<span
						className='user-profile-card__role'
						title={rolesLabel(roles)}
					>
						{userRole}
						{otherRoles > 0 && <span className='user-profile-card__role-more'> +{otherRoles}</span>}
					</span>
				</div>

				<span className={`user-profile-card__chevron ${isMenuOpen ? 'user-profile-card__chevron--open' : ''}`}>
					<ChevronUpIcon />
				</span>
			</button>

			<UserDropdownMenu
				isOpen={isMenuOpen}
				onClose={handleCloseMenu}
				items={menuItems}
				triggerRef={triggerRef}
			/>
		</div>
	);
};

export default UserProfileCard;
